/**
 * SupervisorApprovalsPage — `/mail/supervisor`.
 *
 * Live operator surface backed by service-mail `/v1/supervisor-approvals/*`.
 *
 * The supervisor sees a queue table of all approval requests (with kind/status
 * pills + filter bar). Approve/reject actions open a modal that captures an
 * optional reason (required for reject) and POSTs to the corresponding
 * decision endpoint.
 */
import { type JSX, useEffect, useMemo, useState } from 'react';
import { Check, X, ListChecks, Loader2 } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import {
  approveSupervisorApproval,
  listSupervisorApprovals,
  rejectSupervisorApproval,
  type SupervisorApprovalDto,
  type SupervisorApprovalKind,
  type SupervisorApprovalStatus,
} from '../../lib/api/supervisorApprovals';

const KIND_OPTIONS: { value: SupervisorApprovalKind | ''; label: string }[] = [
  { value: '', label: 'All kinds' },
  { value: 'WEIGHT_ADJUSTMENT', label: 'Weight adjustment' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'STATUS_OVERRIDE', label: 'Status override' },
  { value: 'OTHER', label: 'Other' },
];

const STATUS_OPTIONS: { value: SupervisorApprovalStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

function statusPillClass(status: SupervisorApprovalStatus): string {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-50 text-yellow-700';
    case 'APPROVED':
      return 'bg-green-50 text-green-700';
    case 'REJECTED':
      return 'bg-red-50 text-red-700';
  }
}

interface DecisionModalState {
  request: SupervisorApprovalDto;
  decision: 'APPROVE' | 'REJECT';
}

export function SupervisorApprovalsPage(): JSX.Element {
  const [items, setItems] = useState<readonly SupervisorApprovalDto[]>([]);
  const [statusFilter, setStatusFilter] = useState<SupervisorApprovalStatus | ''>('PENDING');
  const [kindFilter, setKindFilter] = useState<SupervisorApprovalKind | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [modal, setModal] = useState<DecisionModalState | null>(null);
  const [reason, setReason] = useState('');

  async function refresh(): Promise<void> {
    setLoading(true);
    try {
      const res = await listSupervisorApprovals({
        ...(statusFilter && { status: statusFilter }),
        ...(kindFilter && { kind: kindFilter }),
        pageSize: 50,
      });
      setItems(res.items);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load approvals');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [statusFilter, kindFilter]);

  const heading = useMemo(
    () => `${items.length} request${items.length === 1 ? '' : 's'}`,
    [items.length],
  );

  function openDecision(req: SupervisorApprovalDto, decision: 'APPROVE' | 'REJECT'): void {
    setReason('');
    setModal({ request: req, decision });
  }

  async function submitDecision(): Promise<void> {
    if (!modal) return;
    const { request: req, decision } = modal;
    if (decision === 'REJECT' && reason.trim().length === 0) return;
    setBusyId(req.id);
    try {
      if (decision === 'APPROVE') {
        await approveSupervisorApproval(req.id, reason.trim() || undefined);
      } else {
        await rejectSupervisorApproval(req.id, reason.trim());
      }
      setModal(null);
      setReason('');
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Decision failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5" data-testid="supervisor-approvals-page">
      <PageHeader
        title="Supervisor Approvals"
        description="Approve or reject operator escalations awaiting supervisor sign-off."
      />

      <div className="bg-white border border-border rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="status-filter" className="text-xs font-semibold text-gray-700">Status</label>
          <select
            id="status-filter"
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SupervisorApprovalStatus | '')}
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="kind-filter" className="text-xs font-semibold text-gray-700">Kind</label>
          <select
            id="kind-filter"
            data-testid="kind-filter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as SupervisorApprovalKind | '')}
            className="px-2 py-1.5 text-sm border border-border rounded-lg"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <span className="text-xs text-muted ml-auto">{heading}</span>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : null}

      {loading ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center text-sm text-muted">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-8 text-center" data-testid="queue-empty">
          <ListChecks className="w-8 h-8 text-muted mx-auto mb-2" />
          <p className="text-sm font-semibold text-gray-700">Queue clear</p>
          <p className="text-xs text-muted mt-1">No matching approval requests.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="approvals-list">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`approval-row-${it.id}`}
              className="bg-white border border-border rounded-xl p-4 flex items-start justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded bg-blue-50 text-blue-700">
                    {it.kind.replace(/_/g, ' ')}
                  </span>
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded ${statusPillClass(it.status)}`}>
                    {it.status}
                  </span>
                  {it.subjectId ? (
                    <span className="text-[11px] text-muted font-mono">{it.subjectType}:{it.subjectId}</span>
                  ) : null}
                </div>
                <p className="text-sm font-semibold text-gray-900 mt-2">{it.summary}</p>
                <p className="text-xs text-muted mt-1">
                  Requested by {it.requestedByOperatorId}
                  {it.requestedAtOutletCode ? ` · ${it.requestedAtOutletCode}` : ''}
                  {' · '}
                  {new Date(it.createdAt).toLocaleString()}
                </p>
                {it.status !== 'PENDING' && it.decisionReason ? (
                  <p className="text-xs text-muted mt-1 italic">Reason: {it.decisionReason}</p>
                ) : null}
              </div>
              {it.status === 'PENDING' ? (
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openDecision(it, 'APPROVE')}
                    disabled={busyId === it.id}
                    data-testid={`approve-${it.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => openDecision(it, 'REJECT')}
                    disabled={busyId === it.id}
                    data-testid={`reject-${it.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {modal ? (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          data-testid="decision-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {modal.decision === 'APPROVE' ? 'Approve request' : 'Reject request'}
            </h2>
            <p className="text-sm text-gray-700 mb-3">{modal.request.summary}</p>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Reason {modal.decision === 'REJECT' ? '(required)' : '(optional)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="decision-reason"
              rows={3}
              className="w-full px-2 py-1.5 text-sm border border-border rounded-lg"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => { setModal(null); setReason(''); }}
                className="px-3 py-1.5 text-sm font-semibold border border-border rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void submitDecision()}
                disabled={modal.decision === 'REJECT' && reason.trim().length === 0}
                data-testid="decision-submit"
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg text-white ${
                  modal.decision === 'APPROVE'
                    ? 'bg-nipost-green hover:bg-nipost-green-dark'
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {modal.decision === 'APPROVE' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
